import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, Table, Button, Badge } from 'reactstrap';
import { toast } from 'react-toastify';
import moment from 'moment';
import Loader from '../common/Loader';
import TenantSettingsModal from '../common/TenantSettingsModal';
import BZAPIService from '../../services/BZAPIService';

const TenantSettings = () => {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedSetting, setSelectedSetting] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  useEffect(() => {
    loadTenantSettings();
  }, []);

  const loadTenantSettings = async () => {
    try {
      setLoading(true);
      const data = await BZAPIService.getTenantSettings();
      setSettings(data || []);
    } catch (error) {
      console.error('Error loading tenant settings:', error);
      toast.error('Failed to load tenant settings');
      setSettings([]);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSetting = setting => {
    setSelectedSetting(setting);
    setModalOpen(true);
  };

  const handleSaveSetting = async (settingId, newValue) => {
    try {
      setModalLoading(true);

      // Prepare the update payload - send only value and type
      const updateData = {
        value: newValue,
        type: selectedSetting.value.type
      };

      await BZAPIService.updateTenantSetting(settingId, updateData);

      // Update local state
      setSettings(prevSettings =>
        prevSettings.map(setting =>
          setting.id === settingId
            ? { ...setting, value: { ...setting.value, value: newValue }, updatedAt: new Date().toISOString() }
            : setting
        )
      );

      toast.success('Setting updated successfully');
      setModalOpen(false);
    } catch (error) {
      console.error('Error updating setting:', error);
      toast.error('Failed to update setting');
    } finally {
      setModalLoading(false);
    }
  };

  const formatValue = setting => {
    if (!setting.value) return 'N/A';

    const { value, type } = setting.value;

    if (type === 'boolean') {
      return <Badge color={value ? 'success' : 'secondary'}>{value ? 'Enabled' : 'Disabled'}</Badge>;
    }

    if (type === 'string') {
      return value || 'Empty';
    }

    return String(value);
  };

  const getTypeLabel = setting => {
    if (!setting.value?.type) return 'Unknown';
    return setting.value.type.charAt(0).toUpperCase() + setting.value.type.slice(1);
  };

  if (loading) {
    return (
      <Card className="h-100">
        <CardHeader>Tenant Settings</CardHeader>
        <CardBody className="d-flex justify-content-center align-items-center">
          <Loader text="Loading settings..." />
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      <Card className="h-100">
        <CardHeader>Tenant Settings</CardHeader>
        <CardBody>
          {settings.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted mb-0">No tenant settings found</p>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead>
                  <tr>
                    <th>Setting</th>
                    <th>Type</th>
                    <th>Current Value</th>
                    <th>Last Updated</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {settings.map(setting => (
                    <tr key={setting.id}>
                      <td>
                        <div>
                          <strong>{setting.label}</strong>
                          <br />
                          <small className="text-muted">{setting.id}</small>
                        </div>
                      </td>
                      <td>
                        <Badge color="info" pill>
                          {getTypeLabel(setting)}
                        </Badge>
                      </td>
                      <td>{formatValue(setting)}</td>
                      <td>
                        {setting.updatedAt ? (
                          <small className="text-muted">{moment(setting.updatedAt).format('MMM D, YYYY HH:mm')}</small>
                        ) : (
                          'N/A'
                        )}
                      </td>
                      <td>
                        <Button color="falcon-primary" size="sm" onClick={() => handleEditSetting(setting)}>
                          Edit
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      <TenantSettingsModal
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        setting={selectedSetting}
        onSave={handleSaveSetting}
        isLoading={modalLoading}
      />
    </>
  );
};

export default TenantSettings;
